import { useCallback, useEffect, useRef, useState } from 'react';

export function isForbiddenError(err) {
    const status = err?.response?.status || err?.request?.status;
    return status === 403;
}

/**
 * Runs `fetcher()` (an adminApi call returning an axios promise) whenever `deps`
 * change, and again on `reload()`. Keeps the previous data while reloading so
 * tables do not flash. A 403 is reported separately as `isForbidden`.
 */
export default function useAdminQuery(fetcher, deps = [], enabled = true) {
    const [state, setState] = useState({ data: null, isLoading: enabled, isError: false, isForbidden: false });
    const [tick, setTick] = useState(0);
    const seq = useRef(0);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const mine = ++seq.current;
        setState((s) => ({ ...s, isLoading: true, isError: false, isForbidden: false }));
        fetcher()
            .then((res) => {
                if (mine === seq.current) {
                    setState({ data: res.data, isLoading: false, isError: false, isForbidden: false });
                }
            })
            .catch((err) => {
                if (mine !== seq.current) {
                    return;
                }
                const forbidden = isForbiddenError(err);
                setState((s) => ({ ...s, isLoading: false, isError: !forbidden, isForbidden: forbidden }));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, tick, enabled]);

    const reload = useCallback(() => setTick((t) => t + 1), []);

    return { ...state, reload };
}
